import React, { useState, useEffect } from 'react';
import './App.css';
import CourseList from './CourseList';
import Schedule from './Schedule';

const SCHEDULE_STORAGE_KEY = 'clark-course-scheduler:selected-courses';

const readCachedSchedule = () => {
  try {
    const saved = window.localStorage.getItem(SCHEDULE_STORAGE_KEY);
    if (!saved) return [];

    const parsed = JSON.parse(saved);
    return Array.isArray(parsed) ? parsed : [];
  } catch (error) {
    console.error("Error restoring cached schedule:", error);
    return [];
  }
};

function App() {
  const [selectedCourses, setSelectedCourses] = useState(() => readCachedSchedule());
  const [showRestoreNotice, setShowRestoreNotice] = useState(() => readCachedSchedule().length > 0);
  const [hoveredCourse, setHoveredCourse] = useState(null);
  const [availableCourses, setAvailableCourses] = useState([]); // Store all courses from CourseList

  useEffect(() => {
    try {
      window.localStorage.setItem(SCHEDULE_STORAGE_KEY, JSON.stringify(selectedCourses));
    } catch (error) {
      console.error("Error caching schedule:", error);
    }
  }, [selectedCourses]);

  // Function to download schedule
  const handleDownloadSchedule = () => {
    if (selectedCourses.length === 0) {
      alert("No courses in your schedule to download.");
      return;
    }

    // Extract CRNs from selected courses
    const crnList = selectedCourses.map(course => ({
      CRN: course.CRN,
      Course: course.Course,
      Title: course.Title
    }));
    const scheduleData = JSON.stringify(crnList, null, 2);
    
    // Create a downloadable file
    const blob = new Blob([scheduleData], { type: 'application/json' });
    const url = URL.createObjectURL(blob);
    
    // Create a temporary link and trigger download
    const link = document.createElement('a');
    link.href = url;
    const currentDate = new Date().toISOString().split('T')[0];
    link.download = `clark-schedule-${currentDate}.json`;
    document.body.appendChild(link);
    link.click();
    
    // Clean up
    URL.revokeObjectURL(url);
    document.body.removeChild(link);
  };

  // Function to upload schedule
  const handleUploadSchedule = (event) => {
    const file = event.target.files?.[0];
    if (!file) return;

    const reader = new FileReader();
    reader.onload = (e) => {
      try {
        const savedCourses = JSON.parse(e.target.result);
        
        // Verify the uploaded file format
        if (!Array.isArray(savedCourses) || !savedCourses.every(c => c.CRN)) {
          throw new Error("Invalid schedule format");
        }
        
        // Clear current schedule first if there are courses to restore
        if (savedCourses.length > 0 && selectedCourses.length > 0) {
          if (window.confirm("Replace your current schedule with the uploaded one?")) {
            selectedCourses.forEach(course => {
              removeCourseFromSchedule(course);
            });
          } else {
            if (event.target) event.target.value = '';
            return;
          }
        }
        
        // Find courses by CRN and add them to the schedule
        let restoredCount = 0;
        let notFoundCount = 0;
        
        savedCourses.forEach(savedCourse => {
          const course = availableCourses.find(c => c.CRN === savedCourse.CRN);
          if (course) {
            addCourseToSchedule(course);
            restoredCount++;
          } else {
            notFoundCount++;
            console.warn(`Course with CRN ${savedCourse.CRN} (${savedCourse.Course}) not found in current data.`);
          }
        });
        
        if (restoredCount > 0) {
          alert(`Schedule restored: ${restoredCount} courses added to your schedule.`);
        }
        
        if (notFoundCount > 0) {
          alert(`Note: ${notFoundCount} courses from your saved schedule were not found in the current course data.`);
        }
      } catch (error) {
        console.error("Error loading schedule file:", error);
        alert("Invalid schedule file. Please upload a valid schedule file.");
      }
      
      // Reset the input so the same file can be loaded again
      if (event.target) event.target.value = '';
    };
    
    reader.readAsText(file);
  };

  // Set up global function for ICS import
  useEffect(() => {
    // Define a function to find and add courses by their codes
    window.findAndAddCoursesByCodes = (courseCodes) => {
      console.log("Looking for courses with codes:", courseCodes);
      
      if (!availableCourses || availableCourses.length === 0) {
        console.error("No available courses to search through");
        return;
      }
      
      let addedCount = 0;
      
      // Find each course code in the available courses
      courseCodes.forEach(courseCode => {
        // Normalize the code format for comparison
        const normalizedCode = courseCode.trim().toUpperCase();
        
        // Find matches in available courses
        const matchingCourses = availableCourses.filter(course => {
          // Extract just the course code (e.g., "CSCI 120") for comparison
          const courseParts = course.Course?.toString().trim().toUpperCase();
          return courseParts === normalizedCode;
        });
        
        console.log(`Found ${matchingCourses.length} matches for "${normalizedCode}"`);
        
        // Add the first matching course to the schedule
        if (matchingCourses.length > 0) {
          // Check if already in schedule to avoid duplicates
          if (!selectedCourses.some(c => c.Course === matchingCourses[0].Course && c.Section === matchingCourses[0].Section)) {
            addCourseToSchedule(matchingCourses[0]);
            addedCount++;
          }
        }
      });
      
      console.log(`Added ${addedCount} courses to schedule`);
      
      // Return feedback
      return {
        totalCodes: courseCodes.length,
        addedCount: addedCount
      };
    };
    
    return () => {
      // Clean up global function
      delete window.findAndAddCoursesByCodes;
    };
  }, [availableCourses, selectedCourses]);

  // Function to receive all courses from CourseList
  const handleCoursesLoaded = (courses) => {
    setAvailableCourses(courses);
  };

  const addCourseToSchedule = (course) => {
    setSelectedCourses((currentCourses) => {
      if (currentCourses.some(c => c.CRN === course.CRN)) {
        return currentCourses;
      }

      // Create a normalized version of the course with consistent data formats
      const normalizedCourse = {
        ...course,
        CRN: course.CRN || `temp-${Math.random().toString(36).substr(2, 9)}`,
        // We'll keep Days as is for now to handle more complex structures
        Days: course.Days
      };
      
      // For courses with multiple meeting patterns
      if (course.Days && course.Times) {
        // Analyze the data to see if it represents multiple meeting patterns
        let hasMultiplePatterns = false;
        
        if (typeof course.Days === 'string' && typeof course.Times === 'string') {
          // For cases like Days: "MRF", Times: "13:25-14:40" and Days: "MRF", Times: "14:50-16:05"
          // We assume this case has multiple patterns - this needs to be structured
          hasMultiplePatterns = true;
        }
        
        if (hasMultiplePatterns) {
          // Create a schedule array to store the day-to-time mapping
          normalizedCourse.schedule = [];
          
          // Split up by days first
          let dayList = typeof course.Days === 'string' ? course.Days.split('') : 
                       Array.isArray(course.Days) ? course.Days : [course.Days];
          
          // Then by times
          let timeList = [];
          if (typeof course.Times === 'string') {
            if (course.Times.includes('-')) {
              timeList = [course.Times]; // Simple time range
            }
          } else if (Array.isArray(course.Times)) {
            timeList = course.Times;
          }
          
          // If we have one time for multiple days, assign the same time to all days
          if (timeList.length === 1 && dayList.length > 1) {
            dayList.forEach(day => {
              normalizedCourse.schedule.push({
                day,
                time: timeList[0]
              });
            });
          }
          // If we have multiple times for multiple days, match them if possible
          else if (timeList.length > 1 && dayList.length > 1) {
            // Match each day with its corresponding time if available
            dayList.forEach((day, index) => {
              const time = index < timeList.length ? timeList[index] : timeList[0];
              normalizedCourse.schedule.push({
                day,
                time
              });
            });
          }
          // Default fallback
          else {
            // Just use the first time for all days
            const time = timeList.length > 0 ? timeList[0] : 
                        (typeof course.Times === 'string' ? course.Times : null);
            
            if (time) {
              dayList.forEach(day => {
                normalizedCourse.schedule.push({
                  day,
                  time
                });
              });
            }
          }
          
          console.log("Created day-to-time schedule:", normalizedCourse.schedule);
        }
      }
      
      // Keep the original Times field for compatibility
      normalizedCourse.Times = course.Times;
      
      console.log("Adding normalized course to schedule:", normalizedCourse);
      return [...currentCourses, normalizedCourse];
    });
  };

  const removeCourseFromSchedule = (course) => {
    setSelectedCourses((currentCourses) => currentCourses.filter(c => c.CRN !== course.CRN));
  };

  const clearSchedule = () => {
    setSelectedCourses([]);
    setHoveredCourse(null);
    setShowRestoreNotice(false);
    try {
      window.localStorage.removeItem(SCHEDULE_STORAGE_KEY);
    } catch (error) {
      console.error("Error clearing cached schedule:", error);
    }
  };

  // Course hover handler
  const handleCourseHover = (course) => {
    setHoveredCourse(course);
  };
  
  // Clear hover state
  const handleCourseLeave = () => {
    setHoveredCourse(null);
  };
  
  return (
    <div className="App">
      <header className="app-header">
        <h1>Clark University Course Scheduler</h1>
      </header>

      {showRestoreNotice && (
        <div className="restore-notice" role="status">
          <span>
            Restored {selectedCourses.length} course{selectedCourses.length === 1 ? '' : 's'} from this browser.
          </span>
          <button
            className="restore-notice-dismiss"
            onClick={() => setShowRestoreNotice(false)}
            aria-label="Dismiss restored schedule notice"
          >
            Dismiss
          </button>
        </div>
      )}
      
      <div className="app-content">
        <div className="course-catalog-section">
          <CourseList 
            addCourseToSchedule={addCourseToSchedule} 
            selectedCourses={selectedCourses}
            removeCourseFromSchedule={removeCourseFromSchedule}
            onCoursesLoaded={handleCoursesLoaded}
          />
        </div>
        
        <div className="schedule-section">
          {/* <h2>My Schedule {selectedCourses.length > 0 && `(${selectedCourses.length} courses)`}</h2> */}
          <Schedule 
            courses={selectedCourses} 
            removeCourse={removeCourseFromSchedule}
            hoveredCourse={hoveredCourse}
            addCourse={addCourseToSchedule}
            onDownloadSchedule={handleDownloadSchedule}
            onUploadSchedule={handleUploadSchedule}
            onClearSchedule={clearSchedule}
          />
        </div>
      </div>
    </div>
  );
}

export default App;
